import { memo } from "react";

import { IChat } from "@dreamcam/messenger-sdk";
import type { Set } from "immutable";
import { ArrowLeft, X } from "lucide-react";

import { ChatMenu } from "@features/messenger/ChatMenu";

import { Image } from "@components/Image";
import { Typography } from "@components/Typography";

import { useLayoutBreakpoint } from "@hooks/useLayoutBreakpoint";

import { useMessengerStore } from "@storage/global/useMessengerStore";
import { useGetAllCategoriesUsersQuery } from "@storage/queries/messenger";

import { cn } from "@lib/utils";

import { ICON_PATHS } from "@configs/icons";

import { toSetLastSeen } from "./helpers";

interface ChatHeaderProps {
	categoryByUser?: number | null;
	setActivePanel: (isOpenChatList: boolean, currentChat: string | null) => void;
	activeChat: string | null;
	chatInfo: IChat | null;
	typingStatusList: Set<string>;
}

export const ChatHeader = memo(
	({ categoryByUser, setActivePanel, activeChat, chatInfo, typingStatusList }: ChatHeaderProps) => {
		const {
			setMessengerUnsupportedByUser,
			userMsgData,
			messengerUnsupportedByUser,
			isOpenMessenger,
			isMessagesBoxFolded,
			setIsMessagesBoxFolded,
		} = useMessengerStore();

		const categoriesListQuery = useGetAllCategoriesUsersQuery();

		const imageName = messengerUnsupportedByUser.isDisabled
			? messengerUnsupportedByUser.nickname[0]?.toUpperCase()
			: chatInfo?.participant?.username?.[0]?.toUpperCase();
		const logoStyle = {
			width: 40,
			height: 40,
			margin: "0 10px",
			fontSize: 14,
		};
		const { MOBILE, TABLET } = useLayoutBreakpoint();
		const isMobile = MOBILE || TABLET;

		const setCategoryByUser = (category: number | null | undefined) => {
			if (!categoriesListQuery.isSuccess) return;

			const findCategory = categoriesListQuery.data.find((item) => item.id === category);

			if (findCategory) return findCategory.color;
			else return "";
		};

		const onFoldedMessagesBox = () => {
			setIsMessagesBoxFolded(!isMessagesBoxFolded);
			setActivePanel(false, activeChat);
		};

		const isBlinking =
			isMessagesBoxFolded &&
			!messengerUnsupportedByUser.isDisabled &&
			!!chatInfo?.last_message?.sender_id.length &&
			chatInfo?.last_message.read_at === null &&
			chatInfo?.last_message.sender_id !== userMsgData.chat_client_id;

		return (
			<div
				onClick={() => {
					if (isMessagesBoxFolded) {
						setIsMessagesBoxFolded(false);
						setActivePanel(false, activeChat);
					}
				}}
				className={cn(
					`mb-2.5 flex h-12.5 w-full items-center rounded-t-[10px] bg-secondary px-2.5 py-1.25
					shadow-[0_0.25rem_0.25rem_rgba(0,0,0,0.15)]`,
					isMobile && "rounded-t-none",
					isMessagesBoxFolded && "rounded-b-[10px]",
					isMessagesBoxFolded && "cursor-pointer",
					isBlinking && "animate-panel-blink",
				)}
			>
				<div className="flex w-full items-center">
					<button
						className="m-0 flex cursor-pointer border-none bg-transparent p-0 outline-none"
						onClick={(event) => {
							event.stopPropagation();
							setActivePanel(true, null);
							if (messengerUnsupportedByUser.isDisabled) {
								setMessengerUnsupportedByUser({ nickname: "", isDisabled: false, categoryColor: "" });
							}
						}}
					>
						<ArrowLeft size={30} strokeWidth={1.5} stroke="white" className="hover:opacity-60" />
					</button>

					<div
						className="relative flex items-center justify-center rounded-full font-bold"
						style={{
							background: messengerUnsupportedByUser.isDisabled
								? messengerUnsupportedByUser.categoryColor
								: setCategoryByUser(categoryByUser || 0),
							...logoStyle,
						}}
					>
						<Typography type="span" size="sm" weight="bold">
							{imageName}
						</Typography>

						{!messengerUnsupportedByUser.isDisabled ? (
							<>
								{chatInfo?.participant?.is_online ? (
									<span className="absolute right-0 bottom-0">
										<Image width={15} height={15} src={ICON_PATHS.STATUS_ONLINE} alt="status" />
									</span>
								) : null}
							</>
						) : null}
					</div>

					<div className="mr-4 flex flex-col">
						<span
							className={`inline-block max-w-37.5 overflow-hidden text-sm leading-5 font-bold text-ellipsis
								whitespace-nowrap`}
							style={{
								color: messengerUnsupportedByUser.isDisabled
									? messengerUnsupportedByUser.categoryColor
									: setCategoryByUser(categoryByUser),
							}}
						>
							{messengerUnsupportedByUser.isDisabled
								? messengerUnsupportedByUser.nickname
								: chatInfo?.participant?.username}
						</span>

						{!messengerUnsupportedByUser.isDisabled && chatInfo ? (
							<>
								{typingStatusList.has(chatInfo.id) ? (
									<p
										className={`animate-typing-dots inline-flex gap-1 overflow-hidden text-xs leading-4 font-medium
											text-ellipsis whitespace-nowrap text-msg-time`}
									>
										typing
									</p>
								) : (
									<span
										className={`overflow-hidden text-xs leading-4 font-medium text-ellipsis whitespace-nowrap text-white
											opacity-50`}
									>
										{chatInfo.participant?.is_online
											? "online"
											: toSetLastSeen(chatInfo.participant?.last_seen, chatInfo.participant.is_deleted)}
									</span>
								)}
							</>
						) : null}
					</div>

					{!isMessagesBoxFolded && !messengerUnsupportedByUser.isDisabled && chatInfo ? (
						<ChatMenu currentChat={chatInfo} />
					) : null}
				</div>

				<div className="flex">
					{!isMobile ? (
						<>
							{!isMessagesBoxFolded ? (
								<button
									className="m-0 flex cursor-pointer border-none bg-transparent p-0 outline-none"
									onClick={onFoldedMessagesBox}
								>
									<Image width={24} height={24} src={ICON_PATHS.FOLD} alt="fold" />
								</button>
							) : null}
						</>
					) : null}

					<button
						className="m-0 flex cursor-pointer border-none bg-transparent p-0 outline-none"
						onClick={(event) => {
							event.stopPropagation();

							setActivePanel(isOpenMessenger, null);
							if (messengerUnsupportedByUser.isDisabled) {
								setMessengerUnsupportedByUser({ nickname: "", isDisabled: false, categoryColor: "" });
							}
						}}
					>
						<X strokeWidth={1.5} stroke="white" size={24} className="cursor-pointer hover:opacity-60" />
					</button>
				</div>
			</div>
		);
	},
);
